/**
 * หน้าจอบทบาทระบบ — มอบและถอด `ADMIN`/`AUDITOR` (`GET`/`POST /v1/admin/roles`)
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับ `PermissionMatrixScreen`: อ่าน flag จาก `modules`
 * ของ permission set ที่เซิร์ฟเวอร์ส่งมาเท่านั้น ผ่าน `can()` — `RBAC_ADMIN` + `VIEW`
 * เป็นสิทธิ์ของ **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน `RBAC_ADMIN` + `EDIT`
 * เป็นสิทธิ์ของ **การมอบ/ถอด** ซึ่งถูกบังคับข้างในหน้าจอตรงที่ของมัน
 *
 * **สองเรื่องที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้:**
 *
 * 1. **การมอบ `ADMIN` ตัดสิทธิ์เข้าถึงข้อมูลธุรกิจทั้งหมดทันที (D-009)** — ไม่ใช่
 *    "สิทธิ์สูงสุด" แต่เป็นบทบาทของผู้คุม config ที่แลกมาด้วยการเข้าถึงข้อมูลธุรกิจ
 *    ผู้ดูแลที่ไม่รู้ข้อนี้จะมอบ `ADMIN` ให้หัวหน้าฝ่ายผลิตแล้วตัดเขาออกจากงานของ
 *    ตัวเองโดยไม่ตั้งใจ
 * 2. **ถอด `ADMIN` จากคนสุดท้ายไม่ได้** — เซิร์ฟเวอร์ตอบ
 *    `422 LAST_ADMIN_CANNOT_BE_REMOVED` และหน้าจอต้องแสดงการปฏิเสธนั้นเป็นข้อความ
 *    ของมันเอง **โดยไม่ลบบทบาทออกจากตารางก่อนเซิร์ฟเวอร์ยืนยัน** — ตารางที่ลบ
 *    ทิ้งไปก่อนแล้วขึ้นข้อความผิดพลาด บอกผู้ดูแลสองเรื่องที่ขัดกันเองในจอเดียว
 *
 * สถานะของตารางจึงมาจาก `GET /admin/roles` เสมอ และเปลี่ยนได้ทางเดียวคือโหลดใหม่
 * **หลัง** ที่ `POST` ตอบสำเร็จ ไม่มีเส้นทาง optimistic update ในไฟล์นี้เลย
 *
 * **สิ่งที่หน้าจอนี้ไม่มีโดยเจตนา**: สร้างแผนก/พนักงาน, ผูกพนักงานเข้าแผนก และ
 * ให้สิทธิ์เข้าถึงโรงงาน — contract ระบุสองอย่างหลังว่าอยู่นอกขอบเขตของรอบนี้
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ — ที่นี่สำคัญเป็นพิเศษ เพราะสิ่งที่หน้าจอนี้แก้คือตัวการ์ดเอง
 */

import { useEffect, useState } from 'react';

import { RbacAdminFailureNotice } from './RbacAdminFailureNotice';
import {
  SYSTEM_ROLE_DESCRIPTORS,
  describeRole,
  groupRoleAssignments,
  removesBusinessAccess,
  roleLabel,
  toRoleGrantBody,
} from './systemRolesView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  fetchRoleAssignments,
  setSystemRole,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import {
  ADMIN_ROLE_CODE,
  RBAC_ADMIN_MODULE,
  type EmployeeSystemRoles,
  type RoleAssignment,
} from '../../lib/rbacAdmin/types';

export interface SystemRolesScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type RolesState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly RoleAssignment[] }
  | RbacAdminFailure;

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
interface PendingChange {
  readonly employeeId: string;
  readonly roleCode: string;
  readonly granted: boolean;
}

interface GrantFailure {
  readonly employeeId: string;
  readonly failure: RbacAdminFailure;
}

export function SystemRolesScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: SystemRolesScreenProps) {
  const canEdit = can(permissions, RBAC_ADMIN_MODULE, 'EDIT');

  const [state, setState] = useState<RolesState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [employeeId, setEmployeeId] = useState('');
  const [roleCode, setRoleCode] = useState<string>(ADMIN_ROLE_CODE);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [grantFailure, setGrantFailure] = useState<GrantFailure | null>(null);
  const [grantResult, setGrantResult] = useState<EmployeeSystemRoles | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังมอบ/ถอดสำเร็จ **ตารางเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง:
    // ผู้ดูแลเพิ่งกดปุ่มไป สิ่งที่เขาต้องเห็นคือผลของมัน ไม่ใช่หน้าจอที่หายไปทั้งจอ
    setState((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void fetchRoleAssignments({ apiBaseUrl, fetchImpl, signal: controller.signal }).then(
      (result) => {
        if (!active || result.status === 'cancelled') {
          return;
        }
        setState(
          result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
        );
      },
    );

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  /**
   * มอบหรือถอดหนึ่งบทบาท — เส้นทางเขียนเส้นเดียวของหน้าจอนี้
   *
   * ทั้งปุ่มบนฟอร์มและปุ่มถอดในตารางเรียกฟังก์ชันนี้ และมันเดินผ่าน
   * `toRoleGrantBody()` เสมอ ไม่มี branch ไหนประกอบ body เอง
   */
  const submitRoleChange = async (rawEmployeeId: string, code: string, granted: boolean) => {
    const body = toRoleGrantBody(rawEmployeeId, code, granted);
    if (body === null || pending !== null || !canEdit) {
      return;
    }

    setPending({ employeeId: body.employee_id, roleCode: body.role_code, granted });
    setGrantFailure(null);
    setGrantResult(null);

    const result = await setSystemRole(body, { apiBaseUrl, fetchImpl });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่แตะตารางเลย** — สิ่งที่ผู้ดูแลเห็นยังเป็นสถานะที่เซิร์ฟเวอร์ยืนยันล่าสุด
      // ซึ่งสำหรับ `LAST_ADMIN_CANNOT_BE_REMOVED` คือ "บทบาทยังอยู่" และนั่นคือความจริง
      setGrantFailure({ employeeId: body.employee_id, failure: result });
      return;
    }

    // อ่านสถานะใหม่จากเซิร์ฟเวอร์ ไม่ใช่จากสิ่งที่เราเพิ่งส่งไป: คำตอบของ `POST`
    // บอกบทบาทที่คนนั้นถืออยู่จริงหลังเปลี่ยน และการโหลดรายการใหม่ทำให้ทั้งตาราง
    // ตรงกับฐานข้อมูล รวมถึงแถวที่ผู้ดูแลอีกคนเพิ่งเปลี่ยนไปพร้อมกัน
    setGrantResult(result.roles);
    setReloadToken((token) => token + 1);
  };

  const selectedRole = describeRole(roleCode);
  const holders = state.status === 'ready' ? groupRoleAssignments(state.items) : [];
  const busy = pending !== null;

  return (
    <div className="cap-page" data-testid="rbac-system-roles">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            บทบาทระบบ (Admin / Auditor){' '}
            <InfoHint
              label="เกี่ยวกับหน้าบทบาทระบบ"
              text={
                <>
                  บทบาทระบบเป็นคนละชั้นกับตารางสิทธิ์ตามแผนก: ตารางสิทธิ์ตอบว่าแผนกไหนทำอะไร
                  กับโมดูลใดได้ ส่วนบทบาทระบบสองตัวนี้เปลี่ยนตำแหน่งของคนคนหนึ่งในระบบทั้งหมด
                  — และ <strong>{ADMIN_ROLE_CODE} ตัดสิทธิ์เข้าถึงข้อมูลธุรกิจทิ้งทั้งหมด</strong>{' '}
                  (D-009) ไม่ใช่สิทธิ์ที่เพิ่มขึ้นจากเดิม
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canEdit ? (
        <div className="cap-notice" data-testid="rbac-roles-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังมอบหรือถอดบทบาทระบบไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_edit</code>{' '}
            ของโมดูล <code className="cap-table__code">{RBAC_ADMIN_MODULE}</code>{' '}
            ฟอร์มมอบบทบาทและปุ่มถอดจึงไม่แสดง
          </p>
          <p className="cap-notice__note">
            การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ
            backend ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {canEdit ? (
        <form
          className="cap-filters"
          data-testid="rbac-role-grant-form"
          aria-label="มอบบทบาทระบบให้พนักงาน"
          onSubmit={(event) => {
            event.preventDefault();
            void submitRoleChange(employeeId, roleCode, true);
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสพนักงาน</span>
            <input
              className="cap-input"
              name="employee_id"
              data-testid="rbac-role-employee-id"
              value={employeeId}
              autoComplete="off"
              onChange={(event) => {
                setEmployeeId(event.target.value);
              }}
            />
            <InfoHint
              label="เกี่ยวกับรหัสพนักงาน"
              text="รหัสที่องค์กรกำหนดเอง (D-039) ไม่ใช่ตัวเลขที่ระบบสร้าง — รหัสที่ไม่มีอยู่จริง จะถูกเซิร์ฟเวอร์ปฏิเสธด้วย 404"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">บทบาทระบบ</span>
            <select
              className="cap-input"
              name="role_code"
              data-testid="rbac-role-code"
              value={roleCode}
              onChange={(event) => {
                setRoleCode(event.target.value);
              }}
            >
              {SYSTEM_ROLE_DESCRIPTORS.map((role) => (
                <option key={role.code} value={role.code}>
                  {role.label}
                </option>
              ))}
            </select>
            {selectedRole === null ? null : (
              <span data-testid="rbac-role-summary">
                <InfoHint label="เกี่ยวกับบทบาทที่เลือก" text={selectedRole.summary} />
              </span>
            )}
          </label>

          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="rbac-role-grant"
            disabled={busy || employeeId.trim() === ''}
          >
            {busy && pending?.granted === true ? 'กำลังมอบบทบาท…' : 'มอบบทบาท'}
          </button>
        </form>
      ) : null}

      {/**
       * D-009 พูดออกมาตรงจุดที่ผู้ดูแลกำลังจะกด — ไม่ใช่เชิงอรรถท้ายหน้า
       *
       * ข้อความนี้ขึ้นเมื่อบทบาทที่เลือกอยู่เป็นบทบาทที่ตัดสิทธิ์ธุรกิจ ซึ่งอ่านจาก
       * คำอธิบายของบทบาท ไม่ใช่จากการเทียบรหัสในไฟล์นี้
       */}
      {canEdit && removesBusinessAccess(roleCode) ? (
        <div className="cap-notice" data-testid="rbac-admin-role-warning" role="status">
          <StatusBadge tone="warning">อ่านก่อนกด</StatusBadge>
          <h2 className="cap-notice__title">
            {roleLabel(roleCode)} เป็นบทบาทดูแลระบบ — ไม่ใช่สิทธิ์ที่เพิ่มขึ้น
          </h2>
          <p className="cap-notice__text">
            พนักงานที่ได้รับบทบาทนี้จะ <strong>ถูกตัดสิทธิ์เข้าถึงข้อมูลธุรกิจทั้งหมดทันที</strong>{' '}
            (D-009): เปิดข้อมูลสินค้า สูตรการผลิต ใบสั่งผลิต คลัง บัญชี และการตามสอบ Lot
            ไม่ได้อีกเลย แม้แผนกของเขาจะยังมีสิทธิ์เหล่านั้นอยู่ในตารางสิทธิ์ก็ตาม
          </p>
          <p className="cap-notice__note">
            ถ้าสิ่งที่ต้องการคือ &quot;ดูข้อมูลธุรกิจได้ทั้งองค์กรเพื่อตรวจสอบ&quot; บทบาทนั้นคือ{' '}
            ผู้ตรวจสอบ (AUDITOR) ไม่ใช่บทบาทนี้
          </p>
        </div>
      ) : null}

      {grantResult === null ? null : (
        <div className="cap-notice" data-testid="rbac-role-grant-result" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            บทบาทของ {grantResult.employee_id} หลังเปลี่ยน:{' '}
            {grantResult.system_roles.length === 0
              ? 'ไม่ถือบทบาทระบบใดแล้ว'
              : grantResult.system_roles.map(roleLabel).join(' และ ')}
          </h2>
          <p className="cap-notice__text">
            รายการนี้มาจากคำตอบของเซิร์ฟเวอร์ ไม่ใช่จากปุ่มที่เพิ่งกด —
            มอบบทบาทที่ถืออยู่แล้วหรือถอดบทบาทที่ไม่ได้ถือ จะไม่เปลี่ยนอะไรและได้ผลลัพธ์เดิม
          </p>
          {grantResult.system_roles.some(removesBusinessAccess) ? (
            <p className="cap-notice__note" data-testid="rbac-role-grant-result-d009">
              {grantResult.employee_id} ถือบทบาทดูแลระบบอยู่ จึงเข้าถึงข้อมูลธุรกิจไม่ได้ (D-009)
            </p>
          ) : null}
        </div>
      )}

      {grantFailure === null ? null : (
        <RbacAdminFailureNotice
          failure={grantFailure.failure}
          context="grant"
          employeeId={grantFailure.employeeId}
        />
      )}

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="rbac-roles-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการบทบาทระบบ…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <RbacAdminFailureNotice
          failure={state}
          context="roles"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <div className="cap-notice" data-testid="rbac-roles-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีผู้ถือบทบาทระบบ</StatusBadge>
          <h2 className="cap-notice__title">ยังไม่มีใครถือบทบาทระบบเลย</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="ผู้ถือบทบาทระบบ">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              ผู้ถือบทบาทระบบ{' '}
              <InfoHint
                label="เกี่ยวกับลำดับของผู้ถือบทบาท"
                text="เรียงตามรหัสพนักงาน — คนหนึ่งถือได้ทั้งสองบทบาท และจะแสดงทั้งสองในแถวเดียว"
              />
            </h2>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="rbac-roles-table">
              <caption className="cap-visually-hidden">
                พนักงานทุกคนที่ถือบทบาทระบบ พร้อมปุ่มถอดบทบาททีละรายการ — การถอด ADMIN
                จากผู้ดูแลคนสุดท้ายที่ยังใช้งานอยู่จะถูกเซิร์ฟเวอร์ปฏิเสธ
              </caption>
              <thead>
                <tr>
                  <th scope="col">พนักงาน</th>
                  <th scope="col">บทบาทที่ถืออยู่</th>
                  <th scope="col">ถอดบทบาท</th>
                </tr>
              </thead>
              <tbody>
                {holders.map((holder) => (
                  <tr key={holder.employee_id} data-testid={`rbac-role-row-${holder.employee_id}`}>
                    <th scope="row" className="cap-table__row-head">
                      {holder.display_name}
                      <br />
                      <code className="cap-table__code">{holder.employee_id}</code>
                    </th>
                    <td>
                      {holder.roles.map((role) => (
                        <span
                          key={role.role_code}
                          data-testid={`rbac-role-badge-${holder.employee_id}-${role.role_code}`}
                        >
                          <StatusBadge
                            tone={removesBusinessAccess(role.role_code) ? 'warning' : 'info'}
                          >
                            {roleLabel(role.role_code)}
                          </StatusBadge>
                        </span>
                      ))}
                      {holder.roles.some((role) => removesBusinessAccess(role.role_code)) ? (
                        <span
                          className="cap-field__hint"
                          data-testid={`rbac-role-d009-note-${holder.employee_id}`}
                        >
                          ถือบทบาทดูแลระบบ จึงเข้าถึงข้อมูลธุรกิจไม่ได้ (D-009)
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {canEdit ? (
                        holder.roles.map((role) => (
                          <button
                            key={role.role_code}
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`rbac-role-revoke-${holder.employee_id}-${role.role_code}`}
                            disabled={busy}
                            onClick={() => {
                              void submitRoleChange(holder.employee_id, role.role_code, false);
                            }}
                          >
                            {busy &&
                            pending?.employeeId === holder.employee_id &&
                            pending.roleCode === role.role_code
                              ? 'กำลังถอด…'
                              : `ถอด ${roleLabel(role.role_code)}`}
                          </button>
                        ))
                      ) : (
                        <span className="cap-field__hint">ดูได้อย่างเดียว</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
